/**
 * Reducer for the campaign creation form (T3.4). Field names intentionally
 * mirror the legacy `backend/static/js/state.js` + form DOM so QA can
 * cross-reference the parity file (`docs/migration/parity/form.md`).
 *
 * Design notes:
 *   - All cascading downstream state lives here so effects in FormPage
 *     stay thin. `SET_ADVERTISER` preserves current setup (user-requested
 *     workflow for cloning campaigns across accounts); `SET_OBJECTIVE`
 *     wipes ad-group + creative selections; `SET_SMART_PLUS(true)` forces
 *     creative_mode back to SMART_CREATIVE (legacy `creatives.js:96-99`).
 *   - Creative mode switching also auto-adds the first ad-text row when
 *     switching INTO SMART_CREATIVE with zero rows, mirroring
 *     `creatives.js:88`.
 *   - `validateForm` lives here so the reducer + its validator can be
 *     unit tested without the TanStack Query stack.
 */

export interface CampaignEntry {
  id: string
  name: string
  url: string
}

export interface SelectedCta {
  // The full object-shaped id from the backend; we re-serialise it at
  // submit time via JSON.stringify, one append per CTA (parity: form-
  // submit.js:87).
  id: Record<string, unknown>
  name: string
  value: string
}

export interface CarouselBatch {
  id: string
  index: number
  images: File[]
  music: File | null
}

/** Smallest positive index not already used by an existing batch. */
export function nextCarouselBatchIndex(batches: CarouselBatch[]): number {
  const used = new Set(batches.map((b) => b.index))
  let n = 1
  while (used.has(n)) n += 1
  return n
}

export interface SmartAdText {
  id: number
  text: string
}

export type CreativeMode = "MANUAL" | "SMART_CREATIVE"

export type InterestBehaviorTab =
  | "interests"
  | "purchase_intention"
  | "video"
  | "creator"

export interface FormState {
  advertiserId: string | null
  advertiserLabel: string

  // Step 2
  campaigns: CampaignEntry[]
  campaignNameInput: string
  objectiveType: string | null
  isSmartPlusCampaign: boolean
  isCboCampaign: boolean
  skipLearningPhase: boolean
  /** Smart+ only: auto-add newly generated creatives during delivery. */
  creativeAutoAddToggle: boolean

  // Step 3
  adgroupName: string
  pixelId: string | null
  selectedRegions: string[]
  locationsSearch: string
  selectedAgeGroups: string[]
  budget: string
  conversionBidPrice: string
  /** Interests & behaviors (optional). Empty = broad targeting as before. */
  interestCategoryIds: string[]
  purchaseIntentionKeywordIds: string[]
  videoActionCategoryIds: string[]
  creatorActionCategoryIds: string[]
  /** id → display name for chips across I&B tabs */
  targetingLabels: Record<string, string>
  interestsSearch: string
  purchaseIntentionSearch: string
  videoActionsSearch: string
  creatorActionsSearch: string
  interestBehaviorTab: InterestBehaviorTab
  smartInterestBehaviorEnabled: boolean

  // Step 4
  creativeMode: CreativeMode
  unifiedIdentityId: string | null
  unifiedIdentityType: string
  unifiedIdentityAuthorizedBcId: string
  unifiedAdText: string
  smartAdTexts: SmartAdText[]
  smartAdTextCounter: number
  selectedCtas: SelectedCta[]
  instantFormPageId: string | null
  videoFiles: File[]
  /** Browser-extracted covers paired by video index; null = server fallback. */
  videoCoverFiles: (File | null)[]
  carouselBatches: CarouselBatch[]
  carouselBatchCounter: number
  newIdentityForm: { open: boolean; name: string } | null
  makeAdsUnique: boolean
}

export const GENDER_UNLIMITED = "GENDER_UNLIMITED"
export const ALL_AGES = "ALL_AGES"
/** TikTok lead gen does not allow 13-17 targeting. */
export const LEAD_GEN_EXCLUDED_AGE_GROUP = "AGE_13_17"

export const initialState: FormState = {
  advertiserId: null,
  advertiserLabel: "",

  campaigns: [],
  campaignNameInput: "",
  objectiveType: "WEB_CONVERSIONS",
  isSmartPlusCampaign: false,
  isCboCampaign: false,
  skipLearningPhase: false,
  creativeAutoAddToggle: false,
  /** CreOS uniquify before launch (shared across all campaign chips). */
  makeAdsUnique: false,

  adgroupName: "",
  pixelId: null,
  selectedRegions: [],
  locationsSearch: "",
  selectedAgeGroups: [],
  budget: "",
  conversionBidPrice: "",
  interestCategoryIds: [],
  purchaseIntentionKeywordIds: [],
  videoActionCategoryIds: [],
  creatorActionCategoryIds: [],
  targetingLabels: {},
  interestsSearch: "",
  purchaseIntentionSearch: "",
  videoActionsSearch: "",
  creatorActionsSearch: "",
  interestBehaviorTab: "interests",
  smartInterestBehaviorEnabled: true,

  creativeMode: "MANUAL",
  unifiedIdentityId: null,
  unifiedIdentityType: "CUSTOMIZED_USER",
  unifiedIdentityAuthorizedBcId: "",
  unifiedAdText: "",
  smartAdTexts: [],
  smartAdTextCounter: 0,
  selectedCtas: [],
  instantFormPageId: null,
  videoFiles: [],
  videoCoverFiles: [],
  carouselBatches: [],
  carouselBatchCounter: 0,
  newIdentityForm: null,
}

export type FormAction =
  | { type: "SET_ADVERTISER"; id: string | null; label: string }
  | { type: "SET_CAMPAIGN_NAME_INPUT"; value: string }
  | { type: "ADD_CAMPAIGN"; name: string }
  | { type: "REMOVE_CAMPAIGN"; id: string }
  | { type: "UPDATE_CAMPAIGN_URL"; id: string; url: string }
  | { type: "RESET_CAMPAIGNS" }
  | { type: "SET_OBJECTIVE"; value: string | null }
  | { type: "SET_SMART_PLUS"; value: boolean }
  | { type: "SET_CBO"; value: boolean }
  | { type: "SET_SKIP_LEARNING_PHASE"; value: boolean }
  | { type: "SET_CREATIVE_AUTO_ADD_TOGGLE"; value: boolean }
  | { type: "SET_MAKE_ADS_UNIQUE"; value: boolean }
  | { type: "SET_ADGROUP_NAME"; value: string }
  | { type: "SET_PIXEL"; id: string | null }
  | { type: "TOGGLE_REGION"; id: string }
  | { type: "SET_REGIONS"; ids: string[] }
  | { type: "SET_LOCATIONS_SEARCH"; value: string }
  | { type: "TOGGLE_AGE_GROUP"; id: string }
  | { type: "SET_BUDGET"; value: string }
  | { type: "SET_CONVERSION_BID"; value: string }
  | { type: "SET_INTEREST_BEHAVIOR_TAB"; tab: InterestBehaviorTab }
  | { type: "SET_INTERESTS_SEARCH"; value: string }
  | { type: "SET_PURCHASE_INTENTION_SEARCH"; value: string }
  | { type: "SET_VIDEO_ACTIONS_SEARCH"; value: string }
  | { type: "SET_CREATOR_ACTIONS_SEARCH"; value: string }
  | {
      type: "TOGGLE_TARGETING_OPTION"
      bucket:
        | "interestCategoryIds"
        | "purchaseIntentionKeywordIds"
        | "videoActionCategoryIds"
        | "creatorActionCategoryIds"
      id: string
      name: string
    }
  | {
      type: "SET_TARGETING_OPTIONS"
      bucket:
        | "interestCategoryIds"
        | "purchaseIntentionKeywordIds"
        | "videoActionCategoryIds"
        | "creatorActionCategoryIds"
      items: { id: string; name: string }[]
    }
  | { type: "CLEAR_INTEREST_BEHAVIOR" }
  | { type: "SET_SMART_INTEREST_BEHAVIOR"; value: boolean }
  | { type: "SET_CREATIVE_MODE"; value: CreativeMode }
  | { type: "SET_IDENTITY"; id: string | null; identity_type: string; bc_id: string }
  | { type: "SET_UNIFIED_AD_TEXT"; value: string }
  | { type: "ADD_SMART_AD_TEXT" }
  | { type: "REMOVE_SMART_AD_TEXT"; id: number }
  | { type: "UPDATE_SMART_AD_TEXT"; id: number; text: string }
  | { type: "TOGGLE_CTA"; cta: SelectedCta }
  | { type: "SET_INSTANT_FORM_PAGE"; id: string | null }
  | { type: "ADD_VIDEOS"; files: File[]; covers: (File | null)[] }
  | { type: "REMOVE_VIDEO"; index: number }
  | { type: "ADD_CAROUSEL_BATCH"; seed?: { images: File[]; music: File | null } }
  | { type: "DUPLICATE_CAROUSEL_BATCH"; id: string }
  | { type: "REMOVE_CAROUSEL_BATCH"; id: string }
  | { type: "UPDATE_CAROUSEL_IMAGES"; id: string; images: File[] }
  | { type: "UPDATE_CAROUSEL_MUSIC"; id: string; music: File | null }
  | { type: "REORDER_CAROUSEL_IMAGES"; id: string; from: number; to: number }
  | { type: "OPEN_NEW_IDENTITY" }
  | { type: "UPDATE_NEW_IDENTITY_NAME"; value: string }
  | { type: "CLOSE_NEW_IDENTITY" }

/** Wipe selections that change on objective switch. */
function clearDownstreamOfObjective(state: FormState): FormState {
  return {
    ...state,
    pixelId: null,
    selectedRegions: [],
    selectedCtas: [],
  }
}

function toggleCreativeModeEffects(
  state: FormState,
  mode: CreativeMode,
): FormState {
  if (mode === "SMART_CREATIVE" && state.smartAdTexts.length === 0) {
    const id = state.smartAdTextCounter + 1
    return {
      ...state,
      creativeMode: mode,
      smartAdTextCounter: id,
      smartAdTexts: [{ id, text: "" }],
    }
  }
  return { ...state, creativeMode: mode }
}

export function formReducer(state: FormState, action: FormAction): FormState {
  switch (action.type) {
    case "SET_ADVERTISER": {
      if (action.id === state.advertiserId && action.label === state.advertiserLabel) {
        return state
      }
      // Keep all existing setup so users can quickly clone campaigns
      // to another ad account without re-entering every field.
      return { ...state, advertiserId: action.id, advertiserLabel: action.label }
    }

    case "SET_CAMPAIGN_NAME_INPUT":
      return { ...state, campaignNameInput: action.value }

    case "ADD_CAMPAIGN": {
      const name = action.name.trim()
      if (!name) return state
      const id =
        typeof crypto !== "undefined" && "randomUUID" in crypto
          ? crypto.randomUUID()
          : `c_${Date.now()}_${Math.random().toString(36).slice(2)}`
      return {
        ...state,
        campaigns: [...state.campaigns, { id, name, url: "" }],
        campaignNameInput: "",
      }
    }

    case "REMOVE_CAMPAIGN":
      return {
        ...state,
        campaigns: state.campaigns.filter((c) => c.id !== action.id),
      }

    case "UPDATE_CAMPAIGN_URL":
      return {
        ...state,
        campaigns: state.campaigns.map((c) =>
          c.id === action.id ? { ...c, url: action.url } : c,
        ),
      }

    case "RESET_CAMPAIGNS":
      return {
        ...state,
        campaigns: [],
        campaignNameInput: "",
      }

    case "SET_OBJECTIVE": {
      if (action.value === state.objectiveType) return state
      const cleared = clearDownstreamOfObjective(state)
      const selectedAgeGroups =
        action.value === "LEAD_GENERATION"
          ? cleared.selectedAgeGroups.filter(
              (id) => id !== LEAD_GEN_EXCLUDED_AGE_GROUP,
            )
          : cleared.selectedAgeGroups
      return {
        ...cleared,
        objectiveType: action.value,
        selectedAgeGroups,
      }
    }

    case "SET_SMART_PLUS": {
      if (action.value) {
        // Smart+ forces SMART_CREATIVE.
        const next = toggleCreativeModeEffects(state, "SMART_CREATIVE")
        return { ...next, isSmartPlusCampaign: true }
      }
      return { ...state, isSmartPlusCampaign: false }
    }

    case "SET_CBO":
      return { ...state, isCboCampaign: action.value }

    case "SET_SKIP_LEARNING_PHASE":
      return { ...state, skipLearningPhase: action.value }

    case "SET_CREATIVE_AUTO_ADD_TOGGLE":
      return { ...state, creativeAutoAddToggle: action.value }

    case "SET_MAKE_ADS_UNIQUE":
      return { ...state, makeAdsUnique: action.value }

    case "SET_ADGROUP_NAME":
      return { ...state, adgroupName: action.value }

    case "SET_PIXEL":
      return { ...state, pixelId: action.id }

    case "TOGGLE_REGION": {
      const exists = state.selectedRegions.includes(action.id)
      return {
        ...state,
        selectedRegions: exists
          ? state.selectedRegions.filter((r) => r !== action.id)
          : [...state.selectedRegions, action.id],
      }
    }

    case "SET_REGIONS":
      return { ...state, selectedRegions: action.ids }

    case "SET_LOCATIONS_SEARCH":
      return { ...state, locationsSearch: action.value }

    case "TOGGLE_AGE_GROUP": {
      const id = action.id
      const selected = state.selectedAgeGroups
      if (id === ALL_AGES) {
        const isSelected = selected.includes(ALL_AGES)
        return {
          ...state,
          selectedAgeGroups: isSelected ? [] : [ALL_AGES],
        }
      }
      const withoutAll = selected.filter((v) => v !== ALL_AGES)
      const exists = withoutAll.includes(id)
      return {
        ...state,
        selectedAgeGroups: exists
          ? withoutAll.filter((v) => v !== id)
          : [...withoutAll, id],
      }
    }

    case "SET_BUDGET":
      return { ...state, budget: action.value }

    case "SET_CONVERSION_BID":
      return { ...state, conversionBidPrice: action.value }

    case "SET_INTEREST_BEHAVIOR_TAB":
      return { ...state, interestBehaviorTab: action.tab }

    case "SET_INTERESTS_SEARCH":
      return { ...state, interestsSearch: action.value }

    case "SET_PURCHASE_INTENTION_SEARCH":
      return { ...state, purchaseIntentionSearch: action.value }

    case "SET_VIDEO_ACTIONS_SEARCH":
      return { ...state, videoActionsSearch: action.value }

    case "SET_CREATOR_ACTIONS_SEARCH":
      return { ...state, creatorActionsSearch: action.value }

    case "TOGGLE_TARGETING_OPTION": {
      const list = state[action.bucket]
      const exists = list.includes(action.id)
      const nextList = exists
        ? list.filter((id) => id !== action.id)
        : [...list, action.id]
      const labels = { ...state.targetingLabels }
      if (exists) {
        delete labels[action.id]
      } else {
        labels[action.id] = action.name
      }
      return {
        ...state,
        [action.bucket]: nextList,
        targetingLabels: labels,
      }
    }

    case "SET_TARGETING_OPTIONS": {
      const prev = new Set(state[action.bucket])
      const nextIds = action.items.map((item) => item.id)
      const nextSet = new Set(nextIds)
      const labels = { ...state.targetingLabels }
      for (const id of prev) {
        if (!nextSet.has(id)) delete labels[id]
      }
      for (const item of action.items) {
        labels[item.id] = item.name
      }
      return {
        ...state,
        [action.bucket]: nextIds,
        targetingLabels: labels,
      }
    }

    case "CLEAR_INTEREST_BEHAVIOR":
      return {
        ...state,
        interestCategoryIds: [],
        purchaseIntentionKeywordIds: [],
        videoActionCategoryIds: [],
        creatorActionCategoryIds: [],
        targetingLabels: {},
      }

    case "SET_SMART_INTEREST_BEHAVIOR":
      return { ...state, smartInterestBehaviorEnabled: action.value }

    case "SET_CREATIVE_MODE": {
      if (state.isSmartPlusCampaign) return state
      return toggleCreativeModeEffects(state, action.value)
    }

    case "SET_IDENTITY":
      return {
        ...state,
        unifiedIdentityId: action.id,
        unifiedIdentityType: action.identity_type || "CUSTOMIZED_USER",
        unifiedIdentityAuthorizedBcId: action.bc_id || "",
      }

    case "SET_UNIFIED_AD_TEXT":
      return { ...state, unifiedAdText: action.value }

    case "ADD_SMART_AD_TEXT": {
      if (state.smartAdTexts.length >= 5) return state
      const id = state.smartAdTextCounter + 1
      return {
        ...state,
        smartAdTextCounter: id,
        smartAdTexts: [...state.smartAdTexts, { id, text: "" }],
      }
    }

    case "REMOVE_SMART_AD_TEXT": {
      if (state.smartAdTexts.length <= 1) return state
      return {
        ...state,
        smartAdTexts: state.smartAdTexts.filter((t) => t.id !== action.id),
      }
    }

    case "UPDATE_SMART_AD_TEXT":
      return {
        ...state,
        smartAdTexts: state.smartAdTexts.map((t) =>
          t.id === action.id ? { ...t, text: action.text } : t,
        ),
      }

    case "TOGGLE_CTA": {
      const exists = state.selectedCtas.some(
        (c) => c.name === action.cta.name,
      )
      return {
        ...state,
        selectedCtas: exists
          ? state.selectedCtas.filter((c) => c.name !== action.cta.name)
          : [...state.selectedCtas, action.cta],
      }
    }

    case "SET_INSTANT_FORM_PAGE":
      return { ...state, instantFormPageId: action.id }

    case "ADD_VIDEOS":
      if (action.files.length !== action.covers.length) return state
      return {
        ...state,
        videoFiles: [...state.videoFiles, ...action.files],
        videoCoverFiles: [...state.videoCoverFiles, ...action.covers],
      }

    case "REMOVE_VIDEO":
      return {
        ...state,
        videoFiles: state.videoFiles.filter((_, i) => i !== action.index),
        videoCoverFiles: state.videoCoverFiles.filter((_, i) => i !== action.index),
      }

    case "ADD_CAROUSEL_BATCH": {
      const counter = state.carouselBatchCounter + 1
      const index = nextCarouselBatchIndex(state.carouselBatches)
      const id = `b_${counter}_${Math.random().toString(36).slice(2, 8)}`
      const batch: CarouselBatch = {
        id,
        index,
        images: action.seed?.images ? action.seed.images.slice() : [],
        music: action.seed?.music ?? null,
      }
      return {
        ...state,
        carouselBatchCounter: counter,
        carouselBatches: [...state.carouselBatches, batch],
      }
    }

    case "DUPLICATE_CAROUSEL_BATCH": {
      const source = state.carouselBatches.find((b) => b.id === action.id)
      if (!source) return state
      const counter = state.carouselBatchCounter + 1
      const index = nextCarouselBatchIndex(state.carouselBatches)
      const id = `b_${counter}_${Math.random().toString(36).slice(2, 8)}`
      return {
        ...state,
        carouselBatchCounter: counter,
        carouselBatches: [
          ...state.carouselBatches,
          {
            id,
            index,
            images: source.images.slice(),
            music: source.music,
          },
        ],
      }
    }

    case "REMOVE_CAROUSEL_BATCH": {
      const remaining = state.carouselBatches
        .filter((b) => b.id !== action.id)
        .map((b, i) => ({ ...b, index: i + 1 }))
      return {
        ...state,
        carouselBatches: remaining,
      }
    }

    case "UPDATE_CAROUSEL_IMAGES":
      return {
        ...state,
        carouselBatches: state.carouselBatches.map((b) =>
          b.id === action.id ? { ...b, images: action.images } : b,
        ),
      }

    case "UPDATE_CAROUSEL_MUSIC":
      return {
        ...state,
        carouselBatches: state.carouselBatches.map((b) =>
          b.id === action.id ? { ...b, music: action.music } : b,
        ),
      }

    case "REORDER_CAROUSEL_IMAGES": {
      const batch = state.carouselBatches.find((b) => b.id === action.id)
      if (!batch) return state
      if (action.from < 0 || action.from >= batch.images.length) return state
      if (action.to < 0 || action.to >= batch.images.length) return state
      const next = batch.images.slice()
      const [moved] = next.splice(action.from, 1)
      next.splice(action.to, 0, moved)
      return {
        ...state,
        carouselBatches: state.carouselBatches.map((b) =>
          b.id === action.id ? { ...b, images: next } : b,
        ),
      }
    }

    case "OPEN_NEW_IDENTITY":
      return { ...state, newIdentityForm: { open: true, name: "" } }

    case "UPDATE_NEW_IDENTITY_NAME":
      return {
        ...state,
        newIdentityForm: state.newIdentityForm
          ? { ...state.newIdentityForm, name: action.value }
          : { open: true, name: action.value },
      }

    case "CLOSE_NEW_IDENTITY":
      return { ...state, newIdentityForm: null }

    default:
      return state
  }
}

export type ValidationResult =
  | { valid: true }
  | { valid: false; error: string }

export const MIN_DAILY_BUDGET = 20
export const MIN_MANUAL_CBO_DAILY_BUDGET = 50

export function minDailyBudget(
  state: Pick<FormState, "isCboCampaign" | "isSmartPlusCampaign">,
): number {
  return state.isCboCampaign && !state.isSmartPlusCampaign
    ? MIN_MANUAL_CBO_DAILY_BUDGET
    : MIN_DAILY_BUDGET
}

/**
 * Mirrors `backend/static/js/form-submit.js:15-74` line-for-line so
 * QA can grep the same strings.
 */
export function validateForm(state: FormState): ValidationResult {
  // Campaign-name controls only render once an account is picked, so check
  // this first or the campaign error would land on a hidden field.
  if (!state.advertiserId) {
    return { valid: false, error: "Please select an ad account" }
  }
  if (state.campaigns.length === 0) {
    return { valid: false, error: "Add at least one campaign name" }
  }
  for (let i = 0; i < state.campaigns.length; i++) {
    const entry = state.campaigns[i]
    if (!entry.name) {
      return { valid: false, error: `Campaign ${i + 1} must have a name` }
    }
    if (!entry.url || !/^https?:\/\//i.test(entry.url)) {
      return {
        valid: false,
        error: `Campaign ${i + 1} must have a valid URL`,
      }
    }
  }

  const minBudget = minDailyBudget(state)
  const budgetValue = parseFloat(state.budget)
  if (Number.isNaN(budgetValue) || budgetValue < minBudget) {
    return { valid: false, error: `Budget must be at least $${minBudget} per day` }
  }

  if (state.selectedRegions.length === 0) {
    return { valid: false, error: "Please select at least one location." }
  }

  if (state.selectedAgeGroups.length === 0) {
    return { valid: false, error: "Please select at least one age group." }
  }

  const hideCarousels =
    state.creativeMode === "SMART_CREATIVE" && !state.isSmartPlusCampaign
  const activeCarousels = hideCarousels
    ? []
    : state.carouselBatches.filter((b) => b.images.length >= 1)
  const totalCreatives = state.videoFiles.length + activeCarousels.length

  if (totalCreatives === 0) {
    return {
      valid: false,
      error: hideCarousels
        ? "Please upload at least one video"
        : "Please upload at least one video or carousel",
    }
  }

  if (!hideCarousels) {
    for (const batch of state.carouselBatches) {
      if (batch.images.length < 1) {
        return {
          valid: false,
          error: `Carousel ${batch.index} must have at least 1 image`,
        }
      }
      if (!batch.music) {
        return {
          valid: false,
          error: `Carousel ${batch.index} must have a music file`,
        }
      }
    }
  }

  if (state.creativeMode === "SMART_CREATIVE") {
    const filtered = state.smartAdTexts.filter((t) => t.text.trim().length > 0)
    if (filtered.length === 0) {
      return {
        valid: false,
        error: "Smart Creative requires at least 1 ad text",
      }
    }
    if (filtered.length > 5) {
      return {
        valid: false,
        error: "Smart Creative supports maximum 5 ad texts",
      }
    }
    const trimmed = filtered.map((t) => t.text.trim())
    if (new Set(trimmed).size !== trimmed.length) {
      return {
        valid: false,
        error: "Ad texts must be unique. TikTok rejects duplicate titles.",
      }
    }
  } else if (state.unifiedAdText.trim().length === 0) {
    return {
      valid: false,
      error: "Please enter ad text.",
    }
  }

  if (state.selectedCtas.length === 0) {
    return {
      valid: false,
      error: "Please select at least one call to action.",
    }
  }

  return { valid: true }
}

/**
 * Build the multipart payload for ONE campaign entry. Mirrors
 * `form-submit.js:77-114`.
 *
 * When `fileManifest` is provided (from `/upload-creatives`), file blobs are
 * omitted and the shared Spaces manifest is sent as `file_manifest_json`
 * so multi-campaign queues do not re-upload creatives.
 */
export type CreativeFileManifest = Record<
  string,
  {
    spaces_key: string
    filename: string
    content_type: string
  }
>

export function appendCreatives(fd: FormData, state: FormState): void {
  for (const part of listCreativeParts(state)) {
    fd.append(part.formKey, part.file)
  }
}

/** Flat list of creative parts used by per-file upload sessions. */
export function listCreativeParts(
  state: FormState,
): { formKey: string; file: File }[] {
  const parts: { formKey: string; file: File }[] = []
  state.videoFiles.forEach((file, index) => {
    parts.push({ formKey: `unified_video_${index}`, file })
    const cover = state.videoCoverFiles[index]
    if (cover) {
      parts.push({ formKey: `video_cover_${index}`, file: cover })
    }
  })
  state.carouselBatches.forEach((batch) => {
    if (batch.images.length >= 1) {
      batch.images.forEach((image, imgIndex) => {
        parts.push({
          formKey: `carousel_batch_${batch.index}_image_${imgIndex}`,
          file: image,
        })
      })
      if (batch.music) {
        parts.push({
          formKey: `carousel_batch_${batch.index}_music`,
          file: batch.music,
        })
      }
    }
  })
  return parts
}

/** Multipart body containing only creative media fields. */
export function buildCreativesFormData(state: FormState): FormData {
  const fd = new FormData()
  appendCreatives(fd, state)
  return fd
}

export function buildFormData(
  state: FormState,
  campaign: CampaignEntry,
  options?: { fileManifest?: CreativeFileManifest },
): FormData {
  const fd = new FormData()

  fd.set("advertiser_id", state.advertiserId ?? "")
  fd.set("objective_type", state.objectiveType ?? "")
  if (state.isSmartPlusCampaign) {
    fd.set("is_smart_plus_campaign", "true")
  }
  if (state.isCboCampaign) {
    fd.set("is_cbo_campaign", "true")
  }
  if (state.skipLearningPhase) {
    fd.set("skip_learning_phase", "true")
  }
  if (state.isSmartPlusCampaign && state.creativeAutoAddToggle) {
    fd.set("creative_auto_add_toggle", "true")
  }
  fd.set("adgroup_name", state.adgroupName)
  fd.set("pixel_id", state.pixelId ?? "")
  state.selectedRegions.forEach((r) => fd.append("locations", r))
  state.selectedAgeGroups.forEach((a) => fd.append("age_groups", a))
  fd.set("gender", GENDER_UNLIMITED)
  fd.set("budget", state.budget)
  if (state.conversionBidPrice) {
    fd.set("conversion_bid_price", state.conversionBidPrice)
  }

  const hasInterestBehavior =
    state.interestCategoryIds.length > 0 ||
    state.purchaseIntentionKeywordIds.length > 0 ||
    state.videoActionCategoryIds.length > 0 ||
    state.creatorActionCategoryIds.length > 0
  if (hasInterestBehavior) {
    fd.set(
      "interest_behavior_json",
      JSON.stringify({
        interest_category_ids: state.interestCategoryIds,
        purchase_intention_keyword_ids: state.purchaseIntentionKeywordIds,
        video_action_category_ids: state.videoActionCategoryIds,
        creator_action_category_ids: state.creatorActionCategoryIds,
        smart_interest_behavior_enabled: state.smartInterestBehaviorEnabled,
      }),
    )
  }

  fd.set("unified_identity_id", state.unifiedIdentityId ?? "")
  fd.set("unified_identity_type", state.unifiedIdentityType)
  fd.set("unified_identity_authorized_bc_id", state.unifiedIdentityAuthorizedBcId)
  fd.set(
    "unified_ad_text",
    state.creativeMode === "MANUAL" ? state.unifiedAdText : "",
  )

  fd.set("campaign_name", campaign.name)
  fd.set("unified_landing_page_url", campaign.url)

  state.selectedCtas.forEach((cta) =>
    fd.append("unified_call_to_actions", JSON.stringify(cta.id)),
  )

  fd.set("creative_mode", state.creativeMode)

  if (state.creativeMode === "SMART_CREATIVE") {
    const cleaned = state.smartAdTexts
      .map((t) => t.text.trim())
      .filter((t) => t.length > 0)
    cleaned.forEach((text, index) => {
      fd.append(`ad_text_${index}`, text)
    })
  }

  if (options?.fileManifest) {
    fd.set("file_manifest_json", JSON.stringify(options.fileManifest))
  } else {
    appendCreatives(fd, state)
  }

  return fd
}
